// src/pages/kpis/produccion/RechazoBarras.jsx
// Port fiel de rechazo.html a React. Misma lógica de cálculo, mismos
// colores/estilos, mismas gráficas — solo cambia la fuente de datos
// (API en vez de arreglo fijo) y los comentarios (base de datos en vez
// de localStorage). El botón "Cargar datos" se quitó.

import { useEffect, useRef, useState, useCallback } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../services/api';

const CAUSES = ['quebrado', 'pesoBajo', 'tostado', 'restos', 'enmielado', 'pesoAlto', 'envasado', 'quemado'];
const CLBLS = ['Quebrado', 'Peso Bajo', 'Tostado', 'Restos', 'Enmielado', 'Peso Alto', 'Envasado', 'Quemado'];
const CCOLORS = ['#6E4F80', '#185FA5', '#C08A2B', '#3E7C5A', '#E8C57A', '#A98BB8', '#B23A48', '#9A6B1E'];
const RECETAS = ['Original', 'Ligera', 'Chocolate'];
const COL_PROD = '#4A1E5C', COL_RECH = '#C9A9D6', COL_PCT = '#D2742C', COL_WBAR = '#A98BB8', COL_WLINE = '#4A1E5C';
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function N(v, d = 0) {
  return (v || 0).toLocaleString('es-MX', { minimumFractionDigits: d, maximumFractionDigits: d });
}
function axis() {
  return { grid: { color: 'rgba(74,30,92,0.08)' }, ticks: { color: '#6B5B73', font: { family: 'Inter', size: 11 } } };
}
function tip() {
  return { backgroundColor: '#fff', titleColor: '#2A1830', bodyColor: '#52414F', borderColor: '#E6DDD2', borderWidth: 1, padding: 10, cornerRadius: 8, boxPadding: 4 };
}
function isoWeek(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = (d.getDay() + 6) % 7;
  const th = new Date(d); th.setDate(d.getDate() - day + 3);
  const year = th.getFullYear();
  const firstTh = new Date(year, 0, 4);
  const fd = (firstTh.getDay() + 6) % 7; firstTh.setDate(firstTh.getDate() - fd + 3);
  const week = 1 + Math.round((th - firstTh) / (7 * 24 * 3600 * 1000));
  return year + '-' + String(week).padStart(2, '0');
}
function weekMondayKey(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function fmtLargo(s) {
  const p = s.split('-');
  return p[2] + ' ' + MESES[parseInt(p[1]) - 1] + ' ' + p[0];
}

const donutCenter = {
  id: 'donutCenter',
  afterDraw(ch) {
    if (ch.config.type !== 'doughnut') return;
    const { ctx, chartArea: { top, bottom, left, right } } = ch;
    const total = ch.data.datasets[0].data.reduce((a, b) => a + (b || 0), 0);
    const cx = (left + right) / 2, cy = (top + bottom) / 2;
    ctx.save(); ctx.textAlign = 'center';
    ctx.fillStyle = '#2A1830'; ctx.font = 'bold 17px Inter'; ctx.fillText(N(total) + ' kg', cx, cy + 2);
    ctx.fillStyle = '#8A7690'; ctx.font = '10px Inter'; ctx.fillText('total rechazado', cx, cy + 18);
    ctx.restore();
  },
};

export default function RechazoBarras() {
  const [ACTIVE, setActive] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [comentarios, setComentarios] = useState({});
  const [exportando, setExportando] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  const [fTurno, setFTurno] = useState('');
  const [fReceta, setFReceta] = useState('');
  const [fCausa, setFCausa] = useState('');
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');
  const [wDesde, setWDesde] = useState('');
  const [wHasta, setWHasta] = useState('');

  const canvasMat = useRef(null), canvasNoc = useRef(null), canvasDonut = useRef(null), canvasWeekly = useRef(null);
  const chartsRef = useRef({});
  const mainRef = useRef(null);
  const weekKeysRef = useRef([]);

  const [kpis, setKpis] = useState({});
  const [insights, setInsights] = useState([]);
  const [weeklyTotal, setWeeklyTotal] = useState('— kg');

  function toast(msg, err = false) {
    setToastMsg({ msg, err });
    setTimeout(() => setToastMsg(null), 3000);
  }

  // ---------- Carga inicial ----------
  useEffect(() => {
    async function cargar() {
      try {
        setCargando(true);
        const [resData, resCmt] = await Promise.all([
          api.get('/api/produccion/rechazo'),
          api.get('/api/produccion/rechazo/comentarios'),
        ]);
        const data = resData.data.data;
        setActive(data);
        setComentarios(resCmt.data.data || {});
        if (data.length > 0) {
          const dates = data.map((r) => r.fecha).sort();
          setFDesde(dates[0]);
          setFHasta(dates[dates.length - 1]);
          setWDesde(dates[0]);
          setWHasta(dates[dates.length - 1]);
        }
      } catch (err) {
        console.error(err);
        toast('No se pudo cargar el reporte.', true);
      } finally {
        setCargando(false);
      }
    }
    cargar();
  }, []);

  // ---------- Filtro principal ----------
  const filtered = useCallback(() => {
    return ACTIVE.filter((x) => {
      if (fTurno && x.turno !== fTurno) return false;
      if (fReceta && x.receta !== fReceta) return false;
      if (fDesde && x.fecha < fDesde) return false;
      if (fHasta && x.fecha > fHasta) return false;
      return true;
    }).map((x) => {
      if (!fCausa) return x;
      const v = x[fCausa] || 0;
      return { ...x, rechazoTotal: v, pctRechazo: x.mzTotal > 0 ? v / x.mzTotal : 0 };
    });
  }, [ACTIVE, fTurno, fReceta, fCausa, fDesde, fHasta]);

  function resetF() {
    setFTurno(''); setFReceta(''); setFCausa('');
    if (ACTIVE.length > 0) {
      const dates = ACTIVE.map((r) => r.fecha).sort();
      setFDesde(dates[0]); setFHasta(dates[dates.length - 1]);
    }
  }

  // ---------- upsert de gráficas (crea una vez, actualiza en sitio) ----------
  function upsert(key, canvasEl, config) {
    if (!canvasEl) return;
    const ctx = canvasEl.getContext('2d');
    if (chartsRef.current[key]) {
      const ch = chartsRef.current[key];
      ch.data.labels = config.data.labels;
      config.data.datasets.forEach((ds, i) => { if (ch.data.datasets[i]) ch.data.datasets[i].data = ds.data; });
      ch.update();
    } else {
      chartsRef.current[key] = new Chart(ctx, config);
    }
  }

  function comboByDate(data, turno) {
    const recs = data.filter((r) => r.turno === turno);
    const map = {};
    recs.forEach((r) => { if (!map[r.fecha]) map[r.fecha] = { prod: 0, rech: 0 }; map[r.fecha].prod += r.mzTotal; map[r.fecha].rech += r.rechazoTotal; });
    const dates = Object.keys(map).sort();
    return {
      labels: dates.map((d) => d.slice(5)),
      prod: dates.map((d) => map[d].prod),
      rech: dates.map((d) => map[d].rech),
      pct: dates.map((d) => (map[d].prod > 0 ? (map[d].rech / map[d].prod) * 100 : 0)),
    };
  }

  function comboConfig(g) {
    return {
      type: 'bar',
      data: {
        labels: g.labels,
        datasets: [
          { type: 'bar', label: 'Producción', data: g.prod, backgroundColor: COL_PROD, borderRadius: 3, yAxisID: 'y', order: 3,
            datalabels: { anchor: 'end', align: 'end', color: '#4A1E5C', font: { size: 9, weight: '700' }, formatter: (v) => (v > 0 ? N(v) : '') } },
          { type: 'bar', label: 'Rechazo', data: g.rech, backgroundColor: COL_RECH, borderRadius: 3, yAxisID: 'y', order: 3,
            datalabels: { anchor: 'end', align: 'end', color: '#6E4F80', font: { size: 9, weight: '700' }, formatter: (v) => (v > 0 ? N(v) : '') } },
          { type: 'line', label: '% Rechazo', data: g.pct, borderColor: COL_PCT, backgroundColor: COL_PCT, yAxisID: 'y1', tension: 0.35, pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: COL_PCT, borderWidth: 2.5, order: 1,
            datalabels: { anchor: 'end', align: 'top', color: COL_PCT, font: { size: 9, weight: '800' }, formatter: (v) => (v > 0 ? N(v, 1) + '%' : '') } },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, layout: { padding: { top: 24 } },
        scales: {
          x: { ...axis(), ticks: { ...axis().ticks, maxRotation: 50, minRotation: 0, maxTicksLimit: 16 } },
          y: { ...axis(), position: 'left', beginAtZero: true, grace: '12%', title: { display: true, text: 'Kilogramos', color: '#6B5B73', font: { size: 10 } } },
          y1: { position: 'right', beginAtZero: true, grace: '18%', grid: { drawOnChartArea: false }, ticks: { color: COL_PCT, font: { size: 11 }, callback: (v) => v + '%' }, title: { display: true, text: '% Rechazo', color: COL_PCT, font: { size: 10 } } },
        },
        plugins: {
          legend: { labels: { color: '#52414F', font: { family: 'Inter', size: 11 }, usePointStyle: true, padding: 14 } },
          tooltip: { ...tip(), callbacks: { label: (c) => (c.dataset.label === '% Rechazo' ? ` ${c.dataset.label}: ${N(c.parsed.y, 1)}%` : ` ${c.dataset.label}: ${N(c.parsed.y)} kg`) } },
        },
      },
      plugins: [ChartDataLabels],
    };
  }

  // ---------- Render principal (cada vez que cambian los filtros) ----------
  useEffect(() => {
    if (cargando) return;
    const d = filtered();

    const totR = d.reduce((s, r) => s + r.rechazoTotal, 0);
    const totP = d.reduce((s, r) => s + r.mzTotal, 0);
    const pct = totP > 0 ? (totR / totP) * 100 : 0;
    const cmap = {}; CAUSES.forEach((c) => (cmap[c] = d.reduce((s, r) => s + (r[c] || 0), 0)));
    const topC = Object.entries(cmap).sort((a, b) => b[1] - a[1])[0];
    const rmap = {}; RECETAS.forEach((r) => (rmap[r] = d.filter((x) => x.receta === r).reduce((s, x) => s + x.rechazoTotal, 0)));
    const topR = Object.entries(rmap).sort((a, b) => b[1] - a[1])[0];
    const tmap = {}; ['D', 'N'].forEach((t) => { const s = d.filter((x) => x.turno === t); const p = s.reduce((a, x) => a + x.mzTotal, 0); const r = s.reduce((a, x) => a + x.rechazoTotal, 0); tmap[t] = p > 0 ? (r / p) * 100 : 0; });
    const topT = Object.entries(tmap).sort((a, b) => b[1] - a[1])[0];
    const totReproc = d.reduce((s, r) => s + (r.reprocesado || 0), 0);
    const pctReproc = totP > 0 ? (totReproc / totP) * 100 : 0;
    const totSuma = d.reduce((s, r) => s + (r.suma || 0), 0);
    const totMerma = d.reduce((s, r) => s + (r.mermaNoRescatable || 0), 0);
    const pctMerma = totSuma + totMerma > 0 ? (totMerma / (totSuma + totMerma)) * 100 : 0;

    setKpis({
      totR, pct, totP,
      causa: topC ? CLBLS[CAUSES.indexOf(topC[0])] : '—',
      causaSub: topC ? `${N(topC[1])} kg — causa #1` : '',
      receta: topR ? topR[0] : '—',
      recetaSub: topR ? `${N(topR[1])} kg rechazados` : '',
      turno: topT ? (topT[0] === 'D' ? 'Matutino' : 'Nocturno') : '—',
      turnoSub: topT ? `${N(topT[1], 1)}% rechazo promedio` : '',
      totReproc, pctReproc, totMerma, pctMerma,
      semaforo: pct > 12 ? 'red' : pct > 9 ? 'yel' : 'grn',
    });

    upsert('mat', canvasMat.current, comboConfig(comboByDate(d, 'D')));
    upsert('noc', canvasNoc.current, comboConfig(comboByDate(d, 'N')));

    const totals = CAUSES.map((c) => d.reduce((s, r) => s + (r[c] || 0), 0));
    const totalDonut = totals.reduce((a, b) => a + b, 0);
    upsert('donut', canvasDonut.current, {
      type: 'doughnut',
      data: { labels: CLBLS, datasets: [{ data: totals, backgroundColor: CCOLORS, borderColor: '#FBF8F3', borderWidth: 3, hoverOffset: 8,
        datalabels: { color: '#fff', textAlign: 'center', font: { size: 9, weight: '600' }, formatter: (v, ctx) => { const ds = ctx.dataset.data; const sum = ds.reduce((a, b) => a + (b || 0), 0); const p = sum > 0 ? (v / sum) * 100 : 0; return p >= 3 ? N(v) + '\n' + p.toFixed(1) + '%' : ''; } } }] },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '60%',
        plugins: {
          legend: { position: 'right', labels: { color: '#52414F', font: { family: 'Inter', size: 11 }, padding: 8, boxWidth: 12,
            generateLabels: (ch) => { const ds = ch.data.datasets[0].data; const tt = ds.reduce((a, b) => a + (b || 0), 0); return ch.data.labels.map((l, i) => { const p = tt > 0 ? (ds[i] / tt * 100).toFixed(1) : 0; return { text: `${l} · ${p}%`, fillStyle: CCOLORS[i], strokeStyle: CCOLORS[i], lineWidth: 0, index: i }; }); } } },
          tooltip: { ...tip(), callbacks: { label: (c) => { const p = totalDonut > 0 ? (c.parsed / totalDonut * 100).toFixed(1) : 0; return ` ${N(c.parsed)} kg (${p}%)`; } } },
        },
      },
      plugins: [ChartDataLabels, donutCenter],
    });

    const sc = Object.entries(cmap).sort((a, b) => b[1] - a[1]);
    const top1 = sc[0], top2 = sc[1];
    const top1L = CLBLS[CAUSES.indexOf(top1[0])], top2L = CLBLS[CAUSES.indexOf(top2[0])];
    const topRpct = totR > 0 ? (topR[1] / totR * 100).toFixed(1) : 0;
    let dPct = 0, nPct = 0;
    ['D', 'N'].forEach((t) => { const s = d.filter((x) => x.turno === t); const p = s.reduce((a, x) => a + x.mzTotal, 0); const r = s.reduce((a, x) => a + x.rechazoTotal, 0); if (t === 'D') dPct = p > 0 ? r / p * 100 : 0; else nPct = p > 0 ? r / p * 100 : 0; });
    const peor = nPct > dPct ? 'Nocturno' : 'Matutino';
    const diff = Math.abs(nPct - dPct).toFixed(1);
    const wm = {}; d.forEach((r) => { const k = isoWeek(r.fecha); wm[k] = (wm[k] || 0) + r.rechazoTotal; });
    const topW = Object.entries(wm).sort((a, b) => b[1] - a[1])[0];
    setInsights([
      { t: pct > 12 ? 'al' : pct > 9 ? 'wn' : 'ok', m: `${pct > 12 ? '⚠️' : pct > 9 ? '⚠ ' : '✓ '}% de rechazo global: <strong>${N(pct, 1)}%</strong> sobre <strong>${N(totP)}</strong> kg producidos.` },
      { t: 'al', m: `⚠️ <strong>${top1L}</strong> es la causa #1 con <strong>${N(top1[1])} kg</strong> — ${totR > 0 ? (top1[1] / totR * 100).toFixed(1) : 0}% del total rechazado.` },
      { t: '', m: `📌 La receta <strong>${topR[0]}</strong> acumula el <strong>${topRpct}%</strong> del rechazo (${N(topR[1])} kg).` },
      { t: nPct > dPct ? 'wn' : '', m: `🔄 Turno <strong>${peor}</strong> tiene ${diff}pp más de rechazo que el otro turno. D: ${N(dPct, 1)}% · N: ${N(nPct, 1)}%.` },
      { t: '', m: `📉 Segunda causa: <strong>${top2L}</strong> con ${N(top2[1])} kg (${totR > 0 ? (top2[1] / totR * 100).toFixed(1) : 0}% del total).` },
      topW ? { t: 'wn', m: `📅 Semana pico: <strong>${topW[0]}</strong> con <strong>${N(topW[1])} kg</strong> rechazados.` } : null,
    ].filter(Boolean));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargando, filtered]);

  // ---------- Total semanal (rango independiente) ----------
  useEffect(() => {
    if (cargando || ACTIVE.length === 0) return;
    const data = ACTIVE.filter((r) => {
      if (wDesde && r.fecha < wDesde) return false;
      if (wHasta && r.fecha > wHasta) return false;
      return true;
    });
    const wk = {};
    data.forEach((r) => { const k = weekMondayKey(r.fecha); if (!wk[k]) wk[k] = { prod: 0, rech: 0 }; wk[k].prod += r.mzTotal; wk[k].rech += r.rechazoTotal; });
    const keys = Object.keys(wk).sort();
    const labels = keys.map(isoWeek);
    const rech = keys.map((k) => wk[k].rech);
    const pctArr = keys.map((k) => (wk[k].prod > 0 ? (wk[k].rech / wk[k].prod) * 100 : 0));
    const total = data.reduce((s, r) => s + r.rechazoTotal, 0);
    setWeeklyTotal(N(total) + ' kg');
    weekKeysRef.current = keys;

    upsert('weekly', canvasWeekly.current, {
      type: 'bar',
      data: { labels, datasets: [
        { type: 'bar', label: 'Rechazo', data: rech, backgroundColor: COL_WBAR, borderRadius: 4, yAxisID: 'y', order: 2,
          datalabels: { anchor: 'start', align: 'end', color: '#2A1830', font: { size: 10, weight: '700' }, offset: 4, formatter: (v) => (v > 0 ? N(v) : '') } },
        { type: 'line', label: '% Rechazo', data: pctArr, borderColor: COL_WLINE, backgroundColor: COL_WLINE, borderDash: [6, 4], yAxisID: 'y1', tension: 0.3, pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: COL_WLINE, borderWidth: 2, order: 1,
          datalabels: { anchor: 'end', align: 'top', color: COL_WLINE, font: { size: 10, weight: '700' }, formatter: (v) => (v > 0 ? N(v, 1) + '%' : '') } },
      ] },
      options: {
        responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, layout: { padding: { top: 22 } },
        scales: {
          x: axis(),
          y: { ...axis(), beginAtZero: true, grace: '10%', title: { display: true, text: 'Kilogramos', color: '#6B5B73', font: { size: 10 } } },
          y1: { position: 'right', beginAtZero: true, grace: '18%', grid: { drawOnChartArea: false }, ticks: { color: COL_WLINE, font: { size: 11 }, callback: (v) => v + '%' }, title: { display: true, text: '% Rechazo', color: COL_WLINE, font: { size: 10 } } },
        },
        plugins: {
          legend: { labels: { color: '#52414F', font: { family: 'Inter', size: 11 }, usePointStyle: true, padding: 12 } },
          tooltip: { ...tip(), callbacks: {
            title: (items) => { const ks = weekKeysRef.current; const k = ks[items[0].dataIndex]; if (!k) return ''; const mon = new Date(k + 'T00:00:00'); const fri = new Date(mon); fri.setDate(mon.getDate() + 4); return 'Semana ' + fmtLargo(k) + ' al ' + fmtLargo(fri.toISOString().slice(0, 10)); },
            label: (c) => (c.dataset.label === '% Rechazo' ? ` ${c.dataset.label}: ${N(c.parsed.y, 1)}%` : ` ${c.dataset.label}: ${N(c.parsed.y)} kg`),
          } },
        },
      },
      plugins: [ChartDataLabels],
    });
  }, [cargando, ACTIVE, wDesde, wHasta]);

  // ---------- Comentarios (ahora en base de datos) ----------
  async function agregarComentario(zona) {
    try {
      const res = await api.post('/api/produccion/rechazo/comentarios', { zona, texto: '' });
      setComentarios((prev) => ({ ...prev, [zona]: [...(prev[zona] || []), res.data.data] }));
    } catch (err) {
      console.error(err);
      toast('No se pudo agregar el comentario.', true);
    }
  }
  async function guardarComentario(id, zona, texto) {
    try {
      await api.put(`/api/produccion/rechazo/comentarios/${id}`, { texto });
      setComentarios((prev) => ({ ...prev, [zona]: prev[zona].map((c) => (c.id === id ? { ...c, texto } : c)) }));
    } catch (err) {
      console.error(err);
    }
  }
  async function borrarComentario(id, zona) {
    try {
      await api.delete(`/api/produccion/rechazo/comentarios/${id}`);
      setComentarios((prev) => ({ ...prev, [zona]: prev[zona].filter((c) => c.id !== id) }));
    } catch (err) {
      console.error(err);
      toast('No se pudo borrar el comentario.', true);
    }
  }

  // ---------- Export CSV ----------
  function doExportCSV() {
    const d = filtered();
    const hdr = ['turno', 'fecha', 'receta', 'mzTotal', 'pesoAlto', 'pesoBajo', 'quebrado', 'envasado', 'tostado', 'restos', 'enmielado', 'quemado', 'rechazoTotal', 'pctRechazo', 'reprocesado', 'suma', 'mermaNoRescatable'];
    const rows = [hdr.join(','), ...d.map((r) => hdr.map((k) => r[k]).join(','))];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `dasavena_rechazo_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  }

  // ---------- Export PDF ----------
  async function doExportPDF() {
    setExportando(true);
    await new Promise((r) => setTimeout(r, 120));
    const swaps = [];
    try {
      Object.values(chartsRef.current).forEach((chart) => {
        if (!chart || !chart.canvas) return;
        const c = chart.canvas, w = c.clientWidth, h = c.clientHeight;
        const img = document.createElement('img');
        try { img.src = chart.toBase64Image('image/png', 1); } catch { return; }
        img.style.width = w + 'px'; img.style.height = h + 'px'; img.style.display = 'block';
        c.style.display = 'none';
        c.parentNode.insertBefore(img, c);
        swaps.push({ c, img });
      });
      await Promise.all(swaps.map((s) => s.img.decode().catch(() => {})));
      await new Promise((r) => setTimeout(r, 60));

      const el = mainRef.current;
      const W = el.scrollWidth, H = el.scrollHeight;
      const MAXDIM = 3500, MAXAREA = 12000000;
      let scale = Math.min(2, MAXDIM / W, MAXDIM / H, Math.sqrt(MAXAREA / (W * H)));
      if (!isFinite(scale) || scale <= 0) scale = 1;

      const canvas = await html2canvas(el, { scale, backgroundColor: '#FBF8F3', useCORS: true, logging: false });
      const img = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({ orientation: canvas.width >= canvas.height ? 'l' : 'p', unit: 'px', format: [canvas.width, canvas.height], compress: true });
      pdf.addImage(img, 'JPEG', 0, 0, canvas.width, canvas.height, undefined, 'FAST');
      pdf.save('dasavena_rechazo_' + new Date().toISOString().slice(0, 10) + '.pdf');
      toast('✓ PDF descargado en una sola hoja');
    } catch (e) {
      toast('No se pudo generar el PDF. Intenta en Chrome o Edge.', true);
    } finally {
      swaps.forEach((s) => { try { s.img.remove(); s.c.style.display = ''; } catch {} });
      setExportando(false);
    }
  }

  if (cargando) return <div style={{ padding: 40, fontFamily: 'Inter, sans-serif' }}>Cargando reporte...</div>;

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#FBF8F3', color: '#3A2E42', minHeight: '100vh' }}>
      <style>{`
        .rb-kcard{background:#fff;border:1px solid #E6DDD2;border-radius:12px;padding:18px;position:relative;overflow:hidden;box-shadow:0 2px 14px rgba(74,30,92,0.07)}
        .rb-kcard::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:#4A1E5C;border-radius:12px 12px 0 0}
        .rb-kcard.red::before{background:#B23A48}.rb-kcard.yel::before{background:#C08A2B}.rb-kcard.grn::before{background:#3E7C5A}
        .rb-select{padding:6px 10px;border-radius:8px;font-size:13px;font-weight:500;background:#fff;border:1px solid #E6DDD2;color:#3A2E42;font-family:'Inter',sans-serif}
        .rb-cmt-body:empty:before{content:attr(data-ph);color:#8A7690}
      `}</style>

      {toastMsg && (
        <div style={{ position: 'fixed', bottom: 30, left: '50%', transform: 'translateX(-50%)', background: toastMsg.err ? 'rgba(224,82,82,0.95)' : 'rgba(76,175,130,0.95)', color: '#fff', padding: '9px 18px', borderRadius: 8, fontSize: 12, fontWeight: 500, zIndex: 9999 }}>
          {toastMsg.msg}
        </div>
      )}

      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(251,248,243,0.95)', backdropFilter: 'blur(14px)', borderBottom: '1px solid #E6DDD2', padding: '0 28px', height: 66, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700, color: '#2A1830' }}>Dasavena Gourmet</h1>
            <p style={{ fontSize: 10, color: '#8A7690', letterSpacing: '.8px', textTransform: 'uppercase', marginTop: 1 }}>Reporte de Mermas y Rechazo · Barras de Granola</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, border: '1px solid #A98BB8', color: '#4A1E5C', background: '#EFE9F3' }}>{fDesde && fHasta ? `${fDesde.slice(0, 7)} – ${fHasta.slice(0, 7)}` : '—'}</span>
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, border: '1px solid #E6DDD2', color: '#8A7690', background: '#FAF6F0' }}>{ACTIVE.length} registros</span>
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, border: '1px solid #E6DDD2', color: '#8A7690', background: '#FAF6F0' }}>Corte: {ACTIVE.length ? ACTIVE.map((r) => r.fecha).sort().slice(-1)[0] : '—'}</span>
        </div>
        <div style={{ display: 'flex', gap: 7 }}>
          <button onClick={doExportCSV} style={btnStyle}>⬇ CSV</button>
          <button onClick={doExportPDF} disabled={exportando} style={{ ...btnStyle, background: '#4A1E5C', color: '#fff', borderColor: '#4A1E5C' }}>{exportando ? '...' : '↗ PDF'}</button>
        </div>
      </header>

      <main ref={mainRef} style={{ padding: '22px 28px 60px', maxWidth: 1800, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: '#2A1830' }}>Reporte de mermas y rechazo</h2>
          <p style={{ fontSize: 12, color: '#8A7690', marginTop: 2 }}>Período: {fDesde} a {fHasta}</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 14, marginBottom: 8 }}>
          <div className={`rb-kcard ${kpis.semaforo || ''}`}>
            <div style={kLbl}>Total KGs Rechazados</div><div style={kVal}>{N(kpis.totR)} kg</div><div style={kSub}>{N(kpis.totP)} kg producidos</div>
          </div>
          <div className={`rb-kcard ${kpis.semaforo || ''}`}>
            <div style={kLbl}>% Rechazo Global</div><div style={kVal}>{N(kpis.pct, 1)}%</div><div style={kSub}>umbral alerta: &gt;12%</div>
          </div>
          <div className="rb-kcard"><div style={kLbl}>Causa Principal</div><div style={kVal}>{kpis.causa}</div><div style={kSub}>{kpis.causaSub}</div></div>
          <div className="rb-kcard"><div style={kLbl}>Receta Mayor Rechazo</div><div style={kVal}>{kpis.receta}</div><div style={kSub}>{kpis.recetaSub}</div></div>
          <div className="rb-kcard"><div style={kLbl}>Turno Mayor % Rechazo</div><div style={kVal}>{kpis.turno}</div><div style={kSub}>{kpis.turnoSub}</div></div>
          <div className="rb-kcard"><div style={kLbl}>Kg Reprocesado</div><div style={kVal}>{N(kpis.totReproc)} kg</div><div style={kSub}>{N(kpis.pctReproc, 2)}% del MZ total</div></div>
          <div className="rb-kcard"><div style={kLbl}>Merma No Rescatable</div><div style={kVal}>{N(kpis.totMerma)} kg</div><div style={kSub}>{N(kpis.pctMerma, 2)}% de la merma total</div></div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '13px 18px', borderRadius: 12, background: '#fff', border: '1px solid #E6DDD2', margin: '22px 0', boxShadow: '0 2px 14px rgba(74,30,92,0.07)' }}>
          <FiltroSel label="Turno" value={fTurno} onChange={setFTurno} options={[['', 'Todos'], ['D', 'Matutino (D)'], ['N', 'Nocturno (N)']]} />
          <FiltroSel label="Receta" value={fReceta} onChange={setFReceta} options={[['', 'Todas'], ...RECETAS.map((r) => [r, r])]} />
          <FiltroSel label="Causa" value={fCausa} onChange={setFCausa} options={[['', 'Todas'], ...CAUSES.map((c, i) => [c, CLBLS[i]])]} />
          <FiltroFecha label="Desde" value={fDesde} onChange={setFDesde} />
          <FiltroFecha label="Hasta" value={fHasta} onChange={setFHasta} />
          <button onClick={resetF} style={{ ...btnStyle, marginLeft: 'auto', fontSize: 11 }}>↺ Limpiar</button>
        </div>

        <div style={{ textAlign: 'center', margin: '26px 0 16px' }}>
          <span style={{ display: 'inline-block', background: 'linear-gradient(135deg,#2A1830,#4A1E5C)', color: '#fff', padding: '10px 40px', borderRadius: 24, fontWeight: 700, fontSize: 15 }}>Producción vs Rechazo</span>
        </div>

        <Tarjeta titulo="Turno Matutino (D)" sub="Producción y rechazo en kg · línea = % de rechazo por fecha">
          <div style={{ position: 'relative', height: 300 }}><canvas ref={canvasMat} /></div>
          <ZonaComentarios zona="mat" comentarios={comentarios.mat} onAgregar={agregarComentario} onGuardar={guardarComentario} onBorrar={borrarComentario} oculto={exportando} />
        </Tarjeta>

        <div style={{ marginTop: 18 }}>
          <Tarjeta titulo="Turno Nocturno (N)" sub="Producción y rechazo en kg · línea = % de rechazo por fecha">
            <div style={{ position: 'relative', height: 300 }}><canvas ref={canvasNoc} /></div>
            <ZonaComentarios zona="noc" comentarios={comentarios.noc} onAgregar={agregarComentario} onGuardar={guardarComentario} onBorrar={borrarComentario} oculto={exportando} />
          </Tarjeta>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 18 }}>
          <Tarjeta titulo="Kg de Rechazo por Categoría" sub="% de afectaciones por causa de rechazo">
            <div style={{ position: 'relative', height: 320 }}><canvas ref={canvasDonut} /></div>
            <ZonaComentarios zona="donut" comentarios={comentarios.donut} onAgregar={agregarComentario} onGuardar={guardarComentario} onBorrar={borrarComentario} oculto={exportando} />
          </Tarjeta>
          <Tarjeta titulo="Total de Kg de Rechazo" sub="Rechazo acumulado por semana (lun–dom) y % de rechazo · rango de fechas independiente">
            <div style={{ fontSize: 24, fontWeight: 700, color: '#4A1E5C', margin: '2px 0' }}>{weeklyTotal}</div>
            <div style={{ display: 'flex', gap: 6, margin: '6px 0 8px', flexWrap: 'wrap' }}>
              <FiltroFecha label="Desde" value={wDesde} onChange={setWDesde} />
              <FiltroFecha label="Hasta" value={wHasta} onChange={setWHasta} />
            </div>
            <div style={{ position: 'relative', height: 262 }}><canvas ref={canvasWeekly} /></div>
            <ZonaComentarios zona="weekly" comentarios={comentarios.weekly} onAgregar={agregarComentario} onGuardar={guardarComentario} onBorrar={borrarComentario} oculto={exportando} />
          </Tarjeta>
        </div>

        <div style={{ background: '#fff', border: '1px solid #E6DDD2', borderRadius: 12, padding: 18, marginTop: 18, boxShadow: '0 2px 14px rgba(74,30,92,0.07)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#2A1830' }}>📊 Insights Automáticos</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {insights.map((i, idx) => (
              <div key={idx} style={{ padding: '10px 12px', borderRadius: 8, fontSize: 11.5, lineHeight: 1.6, background: i.t === 'al' ? '#F7E5E7' : i.t === 'wn' ? '#F9F0DD' : i.t === 'ok' ? '#E8F1EC' : '#FAF6F0', color: i.t === 'al' ? '#8a2a36' : i.t === 'wn' ? '#7a5417' : i.t === 'ok' ? '#2c5544' : '#3A2E42', borderLeft: `3px solid ${i.t === 'al' ? '#B23A48' : i.t === 'wn' ? '#C08A2B' : i.t === 'ok' ? '#3E7C5A' : '#E6DDD2'}` }}
                dangerouslySetInnerHTML={{ __html: i.m }} />
            ))}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <Tarjeta titulo="Conclusiones Generales" sub="Comentarios y acciones a tomar del reporte">
            <ZonaComentarios zona="general" comentarios={comentarios.general} onAgregar={agregarComentario} onGuardar={guardarComentario} onBorrar={borrarComentario} oculto={exportando} />
          </Tarjeta>
        </div>
      </main>
    </div>
  );
}

const btnStyle = { padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid #E6DDD2', background: '#fff', color: '#4A1E5C' };
const kLbl = { fontSize: 11, fontWeight: 500, color: '#8A7690', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 9 };
const kVal = { fontSize: 26, fontWeight: 700, lineHeight: 1, marginBottom: 5, color: '#2A1830' };
const kSub = { fontSize: 11, color: '#8A7690' };

function Tarjeta({ titulo, sub, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E6DDD2', borderRadius: 12, padding: 20, boxShadow: '0 2px 14px rgba(74,30,92,0.07)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3, color: '#2A1830' }}>{titulo}</div>
      <div style={{ fontSize: 10.5, color: '#8A7690', marginBottom: 14 }}>{sub}</div>
      {children}
    </div>
  );
}
function FiltroSel({ label, value, onChange, options }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ fontSize: 11, color: '#8A7690', textTransform: 'uppercase' }}>{label}</span>
      <select className="rb-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function FiltroFecha({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <span style={{ fontSize: 11, color: '#8A7690', textTransform: 'uppercase' }}>{label}</span>
      <input type="date" className="rb-select" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function ZonaComentarios({ zona, comentarios = [], onAgregar, onGuardar, onBorrar, oculto }) {
  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {comentarios.map((c) => (
        <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#FAF6F0', border: '1px solid #E6DDD2', borderLeft: '3px solid #A98BB8', borderRadius: 8, padding: '10px 12px' }}>
          <div
            className="rb-cmt-body"
            contentEditable
            suppressContentEditableWarning
            data-ph="Escribe aquí tu comentario o conclusión..."
            style={{ flex: 1, fontSize: 12, lineHeight: 1.6, outline: 'none', minHeight: 20 }}
            onBlur={(e) => onGuardar(c.id, zona, e.target.innerText)}
          >
            {c.texto}
          </div>
          {!oculto && <button onClick={() => onBorrar(c.id, zona)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A7690', fontSize: 13 }}>🗑</button>}
        </div>
      ))}
      {!oculto && (
        <button onClick={() => onAgregar(zona)} style={{ alignSelf: 'flex-start', background: 'none', border: '1px dashed #A98BB8', color: '#4A1E5C', cursor: 'pointer', fontSize: 11.5, fontWeight: 500, padding: '6px 12px', borderRadius: 7 }}>
          ✎ Agregar comentario
        </button>
      )}
    </div>
  );
}